import React from 'react';

import { SocialAccount } from '../../../types/api';
import { VideoProject } from '../../../types/editor';
import PostProgressBar from '../../PostProgressBar';
import { WizardDraft } from '../wizardState';

/* Phase of the publish orchestration. State + business logic live in
 * WizardPage so the wizard's footer can drive the action; this
 * component is view-only and renders the summary + the current
 * status. */
export type PublishPhase =
  | 'idle'
  | 'rendering'
  | 'creating-post'
  | 'attaching'
  | 'publishing'
  | 'scheduling'
  | 'done'
  | 'error';

interface PublishStepProps {
  projectId: number;
  draft: WizardDraft;
  project: VideoProject | null;
  accounts: SocialAccount[];
  phase: PublishPhase;
  progress: number;
  error: string | null;
}

export default function PublishStep({
  projectId, draft, project, accounts, phase, progress, error,
}: PublishStepProps) {
  const doneRenders = (project?.renders || [])
    .filter((r) => r.status === 'done' && r.file_url);

  return (
    <div className="wizard-publish">
      <section className="wizard-publish-summary panel">
        <h3 className="section-label">Review</h3>
        {project && (
          <div className="wizard-publish-row">
            <strong>Project</strong>
            <span>{project.name}</span>
          </div>
        )}
        <div className="wizard-publish-row">
          <strong>Accounts</strong>
          <span>
            {accounts.length > 0
              ? accounts.map((a) => `${a.platform}/${a.account_name}`).join(', ')
              : <span className="muted">none selected</span>}
          </span>
        </div>
        <div className="wizard-publish-row">
          <strong>Caption</strong>
          <span className="wizard-publish-caption">
            {draft.description.trim() || <span className="muted">empty</span>}
          </span>
        </div>
        <div className="wizard-publish-row">
          <strong>When</strong>
          <span>
            {draft.scheduleFor
              ? new Date(draft.scheduleFor).toLocaleString()
              : 'Immediately'}
          </span>
        </div>
        <div className="wizard-publish-row">
          <strong>Aspects rendered</strong>
          <span>
            {doneRenders.length > 0
              ? doneRenders.map((r) => r.aspect).join(', ')
              : <span className="muted">none yet — we'll render the default before posting</span>}
          </span>
        </div>
      </section>

      {phase === 'rendering' && (
        <div className="wizard-publish-progress">
          <p className="muted">Rendering ({Math.round(progress)}%)…</p>
          <PostProgressBar event={{
            post_id: projectId, phase: 'rendering', percent: progress,
            message: 'Encoding video', platform: null, ts: Date.now() / 1000,
          }} />
        </div>
      )}

      {phase === 'creating-post' && <p className="muted">Creating the post draft…</p>}
      {phase === 'attaching' && <p className="muted">Attaching rendered video…</p>}
      {phase === 'publishing' && <p className="muted">Publishing to your accounts…</p>}
      {phase === 'scheduling' && <p className="muted">Scheduling…</p>}

      {phase === 'error' && error && (
        <div className="banner banner--error">{error}</div>
      )}
      {phase === 'done' && (
        <div className="banner banner--info">
          Done — taking you to the dashboard…
        </div>
      )}
    </div>
  );
}
